import { Fetcher, objectToFormData } from '@this/src/util';
import React, { useState } from 'react';
import type { RouteComponentProps } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import type { SigninFormData } from './types';
import UserSigninBlueForm from './user_signin_blue_form';
import Notification from '../../notification';
import Background from './background';

interface Props extends RouteComponentProps {
  serviceId: number;
  error?: string;
}

export type SignInResponse = null | 'LoginFailed' | 'AccountLocked';

const UserSigninBlue: React.FC<Props> = ({ serviceId, error, location }) => {
  const [isPasswordExpired] = useState(() => {
    const params = location.search
      .slice(1) // ?key=bar&key2=foo の先頭の?を取り除く
      .split('&')
      .reduce<{ [k in string]: string }>((acc, item) => {
        const [k, v] = item.split('=');
        acc[k] = v;
        return acc;
      }, {});
    return params.password_expired === 'true';
  });

  React.useEffect(() => {
    if (error) {
      Notification.error(error);
    }
  }, [error]);

  const helmet = (
    <Helmet>
      <title>AI Travelログイン</title>
      <link
        href="https://fonts.googleapis.com/css2?family=Noto+Serif+JP:wght@400;500;700&display=swap"
        rel="stylesheet"
      />
    </Helmet>
  );

  const signin = async ({ email, password }: SigninFormData): Promise<SignInResponse> => {
    const params =
      location.pathname === '/admins/sign_in'
        ? {
            admin: signInParams(email, password, serviceId)
          }
        : location.pathname === '/arrangers/sign_in'
        ? {
            arranger: signInParams(email, password, serviceId)
          }
        : {
            user: signInParams(email, password, serviceId)
          };
    try {
      const res = await Fetcher.post<{ redirect: string; type?: string }>(location.pathname, params);
      if (res.type === 'saml') {
        const forms = document.getElementsByName('signinForm');
        const visibleForm = Array.from(forms).find(form => {
          const htmlForm = form as HTMLFormElement;
          return htmlForm.offsetParent !== null;
        }) as HTMLFormElement;
        if (visibleForm) {
          visibleForm.submit();
        }
        return null;
      }
      window.location.href = res.redirect || '/dashboard';
      return null;
    } catch (e) {
      try {
        const errorText = e.response.data.errors[0];
        if (errorText === 'AccountLocked') {
          return errorText;
        }
        return 'LoginFailed';
      } catch (_) {
        return 'LoginFailed';
      }
    }
  };

  const resetPassword = async (email: string): Promise<boolean> => {
    const params =
      location.pathname === '/admins/sign_in'
        ? {
            'admin[reset_email]': email
          }
        : location.pathname === '/arrangers/sign_in'
        ? {
            'arranger[reset_email]': email
          }
        : {
            'user[reset_email]': email
          };
    try {
      await Fetcher.post<{ redirect: string }>(
        location.pathname.replace('sign_in', 'password/reset_mail'),
        objectToFormData(params)
      );
      return true;
    } catch (_) {
      return false;
    }
  };

  const signInParams = (email: string, password: string, svcId: number) => {
    return {
      service_id: svcId,
      email,
      password
    };
  };

  return (
    <>
      {helmet}
      <div style={{ display: 'flex', minHeight: '100vh', width: '100%' }}>
        {/* モバイル表示 */}
        <div
          style={{
            display: 'block',
            width: '100%',
            background: 'white',
            padding: '0 22px',
            paddingTop: '176px',
            paddingBottom: '176px'
          }}
          className="mobile-only"
        >
          <UserSigninBlueForm
            signin={signin}
            resetPassword={resetPassword}
            serviceId={serviceId}
            isPasswordExpired={isPasswordExpired}
          />
        </div>

        {/* PC表示 */}
        <div
          style={{
            display: 'none',
            width: '100%'
          }}
          className="desktop-only"
        >
          {/* 左側: フォームエリア（白背景） */}
          <div
            style={{
              width: '37.03%',
              background: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '63px',
              minHeight: '100vh'
            }}
          >
            <div style={{ width: '348px' }}>
              <UserSigninBlueForm
                signin={signin}
                resetPassword={resetPassword}
                serviceId={serviceId}
                isPasswordExpired={isPasswordExpired}
              />
            </div>
          </div>

          {/* 右側: ブランドエリア（青背景） */}
          <div
            style={{
              width: '62.97%',
              background: '#0080cb',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 40px',
              minHeight: '100vh',
              overflow: 'hidden'
            }}
          >
            {/* 背景の三角形 */}
            <div
              style={{
                position: 'absolute',
                top: '20%',
                right: '-45px',
                width: '666px',
                height: '497px',
                overflow: 'hidden',
                pointerEvents: 'none'
              }}
            >
              <Background />
            </div>

            {/* メインコンテンツ */}
            <div
              style={{
                position: 'relative',
                zIndex: 1,
                width: '650px',
                color: 'black'
              }}
            >
              <h1
                style={{
                  fontFamily: "'Noto Serif JP', sans-serif",
                  fontWeight: 700,
                  fontSize: '48px',
                  lineHeight: '65px',
                  letterSpacing: '7.2px',
                  margin: '0 0 40px 0'
                }}
              >
                この世界すべての出張に
                <br />
                AI Agentを。
              </h1>
              <p
                style={{
                  fontFamily: "'Noto Serif JP', sans-serif",
                  fontWeight: 500,
                  fontSize: '24px',
                  lineHeight: '65px',
                  margin: 0
                }}
              >
                一人ひとりのデータを整え、学習し、予測し、最適化する。AI
                Agentが面倒な手配や経費精算といった出張のストレスを解決するのはもちろん、蓄積されたデータからビジネスの飛躍と新たなチャンスを創造します。
              </p>
            </div>
          </div>
        </div>
      </div>

      <style>
        {`
          @media (max-width: 768px) {
            .desktop-only {
              display: none !important;
            }
            .mobile-only {
              display: block !important;
            }
          }

          @media (min-width: 769px) {
            .desktop-only {
              display: flex !important;
            }
            .mobile-only {
              display: none !important;
            }
          }
        `}
      </style>
    </>
  );
};

export default UserSigninBlue;
